export type SalesOrderItem = {
  id: number;
  quantity: number;
  unitPrice: number;
  total: number;
  productId: number | null;
  name: string;
  slug: string | null;
};

export type SalesOrderSummary = {
  id: number;
  total: number;
  discount: number;
  amountPaid: number;
  balance: number;
  status: string;
  addedOn: string | null;
  trackingNumber: string;
  paymentType: string;
  orderSource: string;
  deliveryName: string;
  deliveryPhone: string;
  deliveryAddress: string;
  email: string;
  deliveryNote: string;
  buyerPhone: string;
  itemCount: number;
};

export type SalesOrdersResponse = {
  orders: SalesOrderSummary[];
  pagination: {
    page: number;
    pageSize: number;
    totalOrders: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
  summary: {
    totalOrders: number;
    pendingOrders: number;
    paidOrders: number;
    revenueTotal: number;
    thisMonthRevenue: number;
  };
};

export type SalesOrderDetail = SalesOrderSummary & {
  items: SalesOrderItem[];
};

export type SalesOrderUpdateInput = {
  status: "Pending" | "Paid" | "Processing" | "Delivered" | "Cancelled";
  trackingNumber?: string;
  amountPaid: number;
  paymentType?: "" | "MPESA" | "CASH" | "CHEQUE" | "BANK_TRANSFER";
};

export type SalesOrderCreateInput = {
  customerId?: number | null;
  customer?: {
    name: string;
    email?: string;
    phone?: string;
    address?: string;
  } | null;
  deliveryName: string;
  deliveryPhone: string;
  deliveryAddress: string;
  deliveryNote?: string;
  trackingNumber?: string;
  status?: "Pending" | "Paid" | "Processing" | "Delivered" | "Cancelled";
  amountPaid?: number;
  paymentType?: "" | "MPESA" | "CASH" | "CHEQUE" | "BANK_TRANSFER";
  items: Array<{
    productId: number;
    quantity: number;
  }>;
};

async function parseResponse(response: Response) {
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(data?.message || `Request failed: ${response.status}`);
  }

  return data;
}

export async function fetchSalesOrders(query: URLSearchParams) {
  const response = await fetch(`/api/sales/orders?${query.toString()}`, {
    credentials: "include",
    headers: {
      Accept: "application/json"
    }
  });

  return (await parseResponse(response)) as SalesOrdersResponse;
}

export async function fetchSalesOrder(orderId: number) {
  const response = await fetch(`/api/sales/orders/${orderId}`, {
    credentials: "include",
    headers: {
      Accept: "application/json"
    }
  });

  return (await parseResponse(response)) as SalesOrderDetail;
}

export async function updateSalesOrder(orderId: number, input: SalesOrderUpdateInput) {
  const response = await fetch(`/api/sales/orders/${orderId}`, {
    method: "PUT",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify(input)
  });

  return parseResponse(response);
}

export async function createSalesOrder(input: SalesOrderCreateInput) {
  const response = await fetch("/api/sales/orders", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify(input)
  });

  return (await parseResponse(response)) as {
    status: "success";
    orderId: number;
    total: number;
    orderStatus: string;
  };
}
